/**
 * SAT AURA™ · Mentor Cockpit (M3 · INTERNAL PREVIEW)
 * Desktop-first. Zeigt Session-Details, Pattern-Kandidaten, Loops,
 * Cross-Domain, Meta-Kandidaten und Breakpoints. Mentor kann Review-
 * Aktionen und Release ausführen.
 */
import React, { useEffect, useState } from "react";
import {
  auraMentorListSessions, auraMentorGetSession,
  auraMentorReview, auraMentorRelease,
} from "../../../lib/auraApi";


export default function AuraMentorCockpit() {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    auraMentorListSessions().then((r) => setSessions(r.sessions)).catch(() => {});
  }, []);

  const load = async (id) => {
    setBusy(true);
    const s = await auraMentorGetSession(id);
    setCurrent(s);
    setBusy(false);
  };

  const review = async (candidateId, action) => {
    if (!current) return;
    await auraMentorReview(current.session_id, {
      profile_id: current.profile.profile_id,
      candidate_id: candidateId, action,
    });
    load(current.session_id);
  };

  const release = async () => {
    if (!current) return;
    await auraMentorRelease(current.session_id);
    load(current.session_id);
    auraMentorListSessions().then((r) => setSessions(r.sessions));
  };

  return (
    <div data-testid="aura-mentor-cockpit"
          className="min-h-screen grid grid-cols-[280px,1fr] bg-slate-50">
      <aside className="border-r bg-white p-4 space-y-2">
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
          AURA · Preview Cockpit
        </div>
        {sessions.length === 0 && (
          <div className="text-xs text-slate-400">Keine Sessions vorhanden.</div>
        )}
        {sessions.map((s) => (
          <button
            key={s.session_id}
            onClick={() => load(s.session_id)}
            data-testid={`aura-mentor-session-${s.session_id}`}
            className={`block w-full text-left rounded-lg p-2 hover:bg-slate-100 ${
              current?.session_id === s.session_id ? "bg-slate-100" : ""}`}
          >
            <div className="text-xs text-slate-500">{s.state}</div>
            <div className="text-sm font-mono">{s.session_id.slice(0, 8)}…</div>
          </button>
        ))}
      </aside>

      <main className="p-8 space-y-6">
        {busy && <div>Lade …</div>}
        {!busy && !current && (
          <div className="text-slate-500">Wähle links eine Session.</div>
        )}
        {!busy && current && (
          <>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">
                Session · {current.state}
              </div>
              <h1 className="text-2xl font-serif mt-1">
                Klient:in {current.owner_user_id.slice(0, 8)}
              </h1>
            </div>

            <section data-testid="aura-mentor-events">
              <h2 className="text-sm font-semibold mb-2">
                Ereignisse ({current.events.length})
              </h2>
              <div className="space-y-1 max-h-40 overflow-auto text-xs text-slate-600">
                {current.events.map((e) => (
                  <div key={e.event_id}>
                    {e.event_date_or_period} · {e.domain} · {e.trigger} → {e.outcome}
                  </div>
                ))}
              </div>
            </section>

            {current.profile && (
              <>
                <section data-testid="aura-mentor-patterns" className="space-y-3">
                  <h2 className="text-sm font-semibold">Pattern-Kandidaten</h2>
                  {current.profile.pattern_candidates.map((c) => (
                    <div key={c.candidate_id}
                          className="rounded-lg border p-3 flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="font-medium">{c.pattern_family}</div>
                        <div className="text-xs text-slate-500">
                          State: {c.state} · Confidence: {c.evidence_confidence?.toFixed(2)} ·
                          Counter-Impact: {c.recurrence_result?.counter_evidence_impact?.toFixed(2)}
                        </div>
                        {c.client_resonance != null && (
                          <div className="text-xs text-slate-500">
                            Klient-Resonanz: {c.client_resonance.toFixed(2)}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {["SUPPORT", "REJECT", "KEEP_INCONCLUSIVE",
                          "REQUEST_CLARIFICATION"].map((a) => (
                          <button
                            key={a}
                            onClick={() => review(c.candidate_id, a)}
                            data-testid={`aura-review-${a}-${c.candidate_id}`}
                            className="text-xs rounded border px-2 py-1"
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </section>

                {current.profile.meta_pattern_candidates?.length > 0 && (
                  <section data-testid="aura-mentor-meta">
                    <h2 className="text-sm font-semibold">Meta-Kandidat</h2>
                    {current.profile.meta_pattern_candidates.map((m) => (
                      <div key={m.meta_pattern_id} className="text-sm">
                        {m.mechanism_label} · {m.state} · Challenge {m.challenge_result}
                      </div>
                    ))}
                  </section>
                )}

                {current.state === "MENTOR_REVIEW" && (
                  <button
                    onClick={release}
                    data-testid="aura-mentor-release"
                    className="rounded-full bg-slate-900 text-white px-6 py-2 text-sm"
                  >
                    Für Klient:in freigeben
                  </button>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
